// 式神の表示（図鑑と強化で共通）：10種族の一覧・効果の合計と上限・各妖の勧誘率・育成の詳細（進化の段階・加勢の技）。
// 素体（バトルに出る妖）を倒すと勧誘。式神Lv（霊玉）で育ち、決まった Lv で★2・★3 に進化（姿が変わる）。
// 持っているだけで4人全員の能力が上がり、加勢に選ぶと戦いでカットインして技で助ける。
// 表記は「式神Lv」（主Lv・妖力と紛れないように）。
import {
  ENEMIES,
  META_CONFIG,
  MOD_LABEL,
  SHIKIGAMI,
  recruitChance,
  shikiFormName,
  shikiNextEvolve,
  kaseiInfo,
  companionIdOf,
  SHIKIGAMI_IDS,
  difficultyLabel,
  shikiHoldValue,
  shikiLevel,
  shikiLevelCap,
  shikiLevelUpCost,
  type Profile,
  type CharId,
  shikigamiMods,
  shikiHoldTotal,
  shikiPassiveTotal,
  shikiFxText,
} from '@hyakki/core';
import { api } from '../api';
import { provincesOfEnemy } from './worldmap';
import { shikiArtHtml, dexFitStyle } from '../assets';
import { act, state } from '../state';
import { esc, fmt, resIcon } from './dom';
import { icon, nodeIcon } from './icons';
import { openModal, toast } from './overlay';
import { playEvolve } from './shiki-fx';
import { lvBoxHtml, type LvRow } from './lvup';
import { errorText } from './status';
import { bindCompanionActions } from './companion';

/** 合計の欄の短い名前（アイコン付きのものは nodeIcon の絵で意味を補う） */
const SHORT: Record<string, string> = { core: '本陣', neutral: '妖', buildHp: '建物HP', skillPower: '術の威力', buildPower: '建物の効果', boss: 'ボス', taken: '被ダメ', potionHeal: '薬の回復' };
const SHORT_ICON = new Set(['core', 'neutral', 'boss', 'taken']);
const pct = (v: number) => `${Math.round(v * 1000) / 10}%`;
const label = (id: string) => MOD_LABEL[SHIKIGAMI[id]?.effect ?? 'hp'] ?? SHIKIGAMI[id]?.effect ?? '';
const lvOf = (p: Profile | null, id: string) => shikiLevel(p?.shikigami?.[id]);
/** 例「HP+3.2%」（式神Lv での持っているだけの効果。古い表示用） */
export const shikiEffectText = (id: string, level: number) => `${label(id)}+${pct(shikiHoldValue(SHIKIGAMI[id], Math.max(1, level)))}`;
void shikiEffectText;
/** 持っている効果（★で伸びる。4人に同じだけ効く） */
/** 持っているだけで4人に効く能力値（★1は1つ、★2からもう1つ増える。どちらも同じ「効果」として並べる） */
const holdText = (id: string, level: number, star: number) => {
  const def = SHIKIGAMI[id];
  const L = Math.max(1, level);
  const st = Math.max(1, star);
  const a = shikiFxText(def.effect, shikiHoldTotal(def, L, st));
  return st >= 2 ? `${a}・${shikiFxText(def.passive, shikiPassiveTotal(def, L, st))}` : a;
};
export const starsRow = (n: number, max = 3) => `<span class="sk-stars" aria-label="★${n}">${Array.from({ length: max }, (_, i) => `<i class="${i < n ? 'on' : ''}">★</i>`).join('')}</span>`;
/** 「式神Lv12」の札（紛れないよう必ず「式神Lv」） */
export const shikiLvChip = (level: number, cap?: number) => `<span class="sk-lv" aria-label="式神Lv${level}"><small>式神Lv</small>${level}${cap ? `<i>/${cap}</i>` : ''}</span>`;
const roleName = (id: string) => SHIKIGAMI[id]?.role2 ?? '';

export function ownedShiki(p: Profile) {
  return Object.entries(p.shikigami ?? {})
    .filter(([id, s]) => s.star > 0 && SHIKIGAMI[id])
    .sort((a, b) => b[1].star - a[1].star || shikiLevel(b[1]) - shikiLevel(a[1]) || (ENEMIES[a[0]]?.no ?? 0) - (ENEMIES[b[0]]?.no ?? 0));
}
export const ownedShikiCount = (p: Profile | null) => (p ? ownedShiki(p).length : 0);

/** 今の勧誘率（素体を倒した1戦あたり。式神の社・勧誘率3倍込み、札なし） */
export function recruitRate(p: Profile | null, id: string): number {
  if (!SHIKIGAMI[id]) return 0;
  return recruitChance(id, { shrineLevel: p?.village?.levels?.yashiro ?? 0, boost: (p?.entitlements ?? []).includes('recruit3x'), first: !(p?.shikigami?.[id]?.star ?? 0) });
}

/** 効果の合計（持っている効果＋常時効果。効果ごとに上限。4人に同じだけ効く）。主な3効果と、ついているもの */
export function shikiTotalsHtml(p: Profile) {
  const sum = shikigamiMods(p) as Record<string, number>;
  const cap = META_CONFIG.shikigami.cap;
  const keys = [...new Set(['hp', 'atk', 'def', ...Object.keys(sum).filter((k) => sum[k])])].filter((k) => cap[k] !== undefined);
  return `<div class="sk-totals" aria-label="式神の効果の合計">${keys
    .map((k) => {
      const v = Math.abs(sum[k] ?? 0);
      const w = cap[k] ? (v / cap[k]) * 100 : 0;
      const sign = k === 'taken' ? '-' : '+';
      return `<div class="sk-total ${v >= cap[k] - 1e-9 ? 'max' : ''}" title="${esc(MOD_LABEL[k as keyof typeof MOD_LABEL] ?? k)}"><span>${SHORT_ICON.has(k) ? icon(nodeIcon(k)) : ''}${esc(SHORT[k] ?? MOD_LABEL[k as keyof typeof MOD_LABEL] ?? k)}</span><b>${sign}${pct(v)}</b><small>/${pct(cap[k])}</small><i class="sk-bar"><i style="width:${w}%"></i></i></div>`;
    })
    .join('')}</div>`;
}

/**
 * 式神30体の一覧（data-shiki で詳細）。仲間は★の高い順（★5 は光る）、まだの式神は章の順で鍵つき（会ったことがあれば名前、無ければ？？？）。
 * 式神Lv・お供の能力の印と、いまのお供には「お供」の札。sel は選んでいる式神（強化の PC）
 */
export function shikiListHtml(p: Profile, sel?: string | null) {
  const seen = new Set(p.dex ?? []);
  const owned = ownedShiki(p);
  const ownedIds = new Set(owned.map(([id]) => id));
  const rest = SHIKIGAMI_IDS.filter((id) => !ownedIds.has(id)).sort((a, b) => (ENEMIES[a]?.no ?? 0) - (ENEMIES[b]?.no ?? 0));
  const reidama = state.resources.reidama ?? 0;
  const cell = (id: string) => {
    const s = p.shikigami?.[id];
    if (s && s.star > 0) {
      const L = shikiLevel(s);
      // 今の霊玉で式神Lv を上げられる印
      const up = L < shikiLevelCap(s.star) && shikiLevelUpCost(L) <= reidama;
      return `<li><button class="sk-cell ${s.star >= 3 ? 'star5' : ''} ${sel === id ? 'sel' : ''}" data-shiki="${esc(id)}"><span class="thumb"><span class="dex-fit" style="${dexFitStyle(id)}">${shikiArtHtml(id, 56)}</span></span>
        <span class="sk-txt"><b>${esc(shikiFormName(id, s.star))}</b><span class="sk-row">${starsRow(s.star)}${shikiLvChip(L)}${up ? `<i class="sk-up" title="霊玉で式神Lv を上げられます">${icon('plus')}</i>` : ''}</span>
        <small class="sk-fx sk-fx2">${holdText(id, L, s.star).split('・').map((x) => `<i>${esc(x)}</i>`).join('')}</small></span></button></li>`;
    }
    const known = seen.has(id);
    return `<li><button class="sk-cell locked ${sel === id ? 'sel' : ''}" ${known ? `data-shiki="${esc(id)}"` : 'disabled'} aria-label="${known ? `${esc(SHIKIGAMI[id].name)}（まだ仲間でない）` : '未遭遇の式神'}">
      <span class="thumb sk-lock">${known ? shikiArtHtml(id, 56) : ''}${icon('lock')}</span>
      <span class="sk-txt"><b>${known ? esc(SHIKIGAMI[id].name) : '？？？'}</b><small class="sk-where">${roleName(id)}・第${difficultyLabel(SHIKIGAMI[id].chapter)}国</small><small class="sk-fx">${esc(label(id))}</small></span></button></li>`;
  };
  return `<ul class="sk-grid">${[...owned.map(([id]) => id), ...rest].map(cell).join('')}</ul>`;
}

/** 今の霊玉で上げられる先（まとめて）：{to, cost}。1段も上げられなければ to = level */
export function affordableLevel(level: number, cap: number, reidama: number) {
  let to = level;
  let cost = 0;
  while (to < cap && cost + shikiLevelUpCost(to) <= reidama) cost += shikiLevelUpCost(to++);
  return { to, cost };
}


/** ★ごとの解放スキルの一覧（未解放は鍵と「★○で解放」）。奥義は条件も */
/** 式神Lv from→to で変わるもの：持っている効果と、解放済みのスキル（お供・加護・第二の能力・極・奥義）の数字 */
export function shikiLvRows(id: string, from: number, to: number, star: number): LvRow[] {
  const s2 = Math.max(star, shikiStageOfLv(to));
  const a = kaseiInfo(id, from, Math.max(1, star))!;
  const b = kaseiInfo(id, to, s2)!;
  return [
    ...(s2 > star ? [{ label: '進化', before: shikiFormName(id, star), after: `${shikiFormName(id, s2)}（★${s2}）` }] : []),
    { label: '効果', before: holdText(id, from, star), after: holdText(id, to, s2) },
    ...(a.desc !== b.desc ? [{ label: `召喚「${b.name}」`, before: `${a.desc}（${a.uses}回）`, after: `${b.desc}（${b.uses}回）` }] : []),
  ];
}
/** 式神Lv での★（進化の段階） */
const shikiStageOfLv = (L: number) => (L >= META_CONFIG.shikigami.evo.evolveAt[1] ? 3 : L >= META_CONFIG.shikigami.evo.evolveAt[0] ? 2 : 1);

/**
 * ★1〜★3 を1枠ずつ（その姿の絵・名前と、その★での効果・加勢・加護）。
 * 今の姿は強調、まだの姿は鍵と進化の式神Lv（値はその進化の式神Lv で見せる）
 */
function stageListHtml(id: string, L: number, star: number, owned: boolean) {
  const evoAt = META_CONFIG.shikigami.evo.evolveAt;
  const labels = ['', '進化', '最終進化'];
  return `<ol class="skd-stages">${[1, 2, 3]
    .map((st) => {
      const need = st === 1 ? 1 : evoAt[st - 2];
      const got = owned && star >= st;
      const now = owned && star === st;
      const lv = Math.max(L, need);
      const k = kaseiInfo(id, lv, st)!;
      const rows = [
        `<li><small>効果</small><span>${esc(holdText(id, lv, st))}</span></li>`,
        `<li class="k"><small>召喚</small><span><b>${esc(k.name)}</b><em class="skd-when">${esc(k.when)}</em>${esc(k.desc)}（1戦に${k.uses}回）</span></li>`,
        `<li><small>加護</small><span>${esc(k.boon.desc)}</span></li>`,
      ].join('');
      const tag = now ? '<i class="skd-now">今の姿</i>' : got ? '' : `<i class="skd-need">${icon('lock')}式神Lv${need}で</i>`;
      return `<li class="skd-stage ${now ? 'now' : got ? 'got' : 'locked'}">
        <header><span class="skd-st-art">${shikiArtHtml(id, 52, st)}</span><span class="skd-st-name"><em class="skd-st">★${st}</em>${labels[st - 1] ? `<small>${labels[st - 1]}</small>` : ''}<b>${esc(shikiFormName(id, st))}</b></span>${tag}</header>
        <ul class="skd-st-rows">${rows}</ul></li>`;
    })
    .join('')}</ol>`;
}

/**
 * 式神の詳細（図鑑・強化で共通）：顔・★・式神Lv（上限）・持っている効果・霊玉で強化（1回・まとめて上限まで）・★ごとの解放スキル・奥義・勧誘率・お供にする。
 * 小型・中型（式神にならない妖）は何も出さない
 */
export function shikiDetailHtml(p: Profile | null, id: string, charId?: CharId) {
  const def = SHIKIGAMI[id];
  const e = ENEMIES[id];
  if (!e) return '';
  if (!def) return '';
  const st = p?.shikigami?.[id];
  const star = st?.star ?? 0;
  const owned = star > 0;
  const L = lvOf(p, id);
  const cap = shikiLevelCap(star);
  const reidama = state.resources?.reidama ?? 0;
  const evo = shikiNextEvolve(L);
  const kaseiChar = charId ?? p?.oshi;
  const isKasei = !!p && owned && !!kaseiChar && companionIdOf(p, kaseiChar) === id;
  let up = '';
  if (owned) {
    if (L >= cap)
      up = `<p class="skd-max">${icon('check')}式神Lv 最大</p>`;
    else {
      const next = shikiLevelUpCost(L);
      const all = affordableLevel(L, cap, reidama);
      const can = reidama >= next;
      up = `${lvBoxHtml('', shikiLvRows(id, L, L + 1, star))}<div class="skd-up">
        <p class="skd-cost">${resIcon('reidama')}次の式神Lvまで 霊玉<b class="${can ? '' : 'bad'}">${fmt(next)}</b><small>所持 ${fmt(reidama)}</small></p>
        <button class="btn primary" data-shiki-lv="1" data-id="${esc(id)}" data-from="${L}" data-star="${star}" ${can ? '' : 'disabled'}>${icon('plus')}霊玉で強化<small>式神Lv${L + 1}</small></button>
        <button class="btn" data-shiki-lv="max" data-id="${esc(id)}" data-from="${L}" data-star="${star}" ${all.to > L + 1 ? '' : 'disabled'}>${icon('speed')}まとめて強化</button>
      </div>`;
    }
  }
  // 次の式神Lvで変わるものは、強化ボタンの上の表に出す
  const next = '';
  return `<section class="skd ${owned ? 'got' : ''} ${star >= 3 ? 'star5' : ''}" data-skd="${esc(id)}">
    <header class="skd-head"><span class="thumb skd-art">${shikiArtHtml(id, 96, Math.max(1, star))}${owned ? '' : icon('lock')}</span>
      <div class="skd-title"><small>${esc(roleName(id))}・第${difficultyLabel(def.chapter)}国${owned ? '' : '・未加入'}</small><b>${esc(shikiFormName(id, Math.max(1, star)))}</b>${owned ? starsRow(star) : ''}
      ${owned && evo ? `<small class="skd-evo">式神Lv${evo}で「${esc(shikiFormName(id, star + 1))}」に進化</small>` : ''}
      ${owned ? `<div class="skd-lv">${shikiLvChip(L, cap)}<i class="sk-bar"><i style="width:${(L / Math.max(1, cap)) * 100}%"></i></i></div>` : ''}</div></header>
    <p class="skd-eff">${owned ? '効果' : '仲間になると'} <b>${esc(holdText(id, L, star))}</b>${owned ? next : '<small>（式神Lv1。式神Lvで伸びる）</small>'}</p>
    ${up}
    ${stageListHtml(id, owned ? L : 1, star, owned)}
    ${owned ? (isKasei ? `<p class="skd-kasei on">${icon('check')}式神召喚にセット中</p>` : `<button class="btn primary wide skd-kasei" data-comp-set="${esc(id)}" ${kaseiChar ? `data-comp-char="${kaseiChar}"` : ''}>${icon('shiki')}式神召喚にセット</button>`) : ''}
    ${owned ? '' : `<p class="sk-rate skd-foot"><small>${icon('flag')}${esc(provincesOfEnemy(id).map((x) => x.name).join('・') || '—')}</small></p>`}
  </section>`;
}

/** 各妖の「式神」欄（図鑑の詳細）。式神なら育成の詳細、小型・中型は「式神にはならない」 */
export const shikiStatusHtml = (p: Profile | null, id: string) => shikiDetailHtml(p, id);

let busy = false;
/**
 * root の中の [data-shiki-lv]（霊玉で強化：1 か max）を扱う。成功したら Lvアップの演出（押すと飛ばす）→ /api/me を取り直して onDone。
 * from（画面の式神Lv）と nonce を送るので、連打・再送で二重に上がらない
 */
export function bindShikiActions(root: HTMLElement, onDone?: () => void): () => void {
  const h = async (e: Event) => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-shiki-lv]');
    if (!b || b.disabled || busy) return;
    e.stopPropagation();
    const id = b.dataset.id!;
    const from = Number(b.dataset.from);
    // まとめて：いくらで、どこまで、何がどれだけ上がるかを見せてから
    if (b.dataset.shikiLv === 'max') {
      const star = state.profile?.shikigami?.[id]?.star ?? 1;
      const all = affordableLevel(from, shikiLevelCap(star), state.resources?.reidama ?? 0);
      const ok = await lvConfirm(`式神 ${shikiFormName(id, star)}`, `${resIcon('reidama')}霊玉 <b>${fmt(all.cost)}</b>`, lvBoxHtml(`式神Lv${from} → ${all.to}`, shikiLvRows(id, from, all.to, star)));
      if (!ok) return;
    }
    busy = true;
    root.querySelectorAll<HTMLButtonElement>('[data-shiki-lv]').forEach((x) => (x.disabled = true));
    try {
      const nonce = `lv-${id}-${from}-${crypto.getRandomValues(new Uint32Array(1))[0].toString(36)}`;
      await act(api.levelShikigami({ enemyId: id, ...(b.dataset.shikiLv === 'max' ? { toMax: true } : { levels: 1 }), from, nonce }));
      const before = Math.max(1, Math.min(3, Number(b.dataset.star) || 1));
      const after = state.profile?.shikigami?.[id]?.star ?? 1;
      if (after > before) await playEvolve(id, before, after);
    } catch (err) {
      toast(errorText(err), 'error');
    } finally {
      busy = false;
      onDone?.();
    }
  };
  root.addEventListener('click', h);
  return () => root.removeEventListener('click', h);
}

/** まとめて強化の確認：費用と、変わるもの。「強化する」で true */
export function lvConfirm(title: string, costHtml: string, bodyHtml: string): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const m = openModal(`<div class="lv-confirm"><p class="lv-cost">${costHtml}</p>${bodyHtml}<div class="btn-row"><button class="btn" data-no>やめる</button><button class="btn primary" data-yes>${icon('enhance')}強化する</button></div></div>`, {
      title,
      onClose: () => !done && resolve(false),
    });
    m.body.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t.closest('[data-yes]')) {
        done = true;
        m.close();
        resolve(true);
      } else if (t.closest('[data-no]')) m.close();
    });
  });
}

/** 式神の詳細の札（強化・編成）。霊玉で強化の後も開いたまま描き直す */
export function openShikiSheet(id: string, redraw: () => void, charId?: CharId) {
  const html = () => shikiDetailHtml(state.profile, id, charId);
  const m = openModal(html(), { title: `式神 ${SHIKIGAMI[id] ? shikiFormName(id, Math.max(1, state.profile?.shikigami?.[id]?.star ?? 1)) : ''}`, className: 'skd-modal' });
  const again = () => {
    redraw();
    if (m.el.isConnected) m.body.innerHTML = html();
  };
  bindShikiActions(m.body, again);
  bindCompanionActions(m.body, again);
}

